import type { KnownIntegrationKey } from "./agentTools/remote/integrations";

import * as z from "zod";

/**
 * The integrations that have a Home workflow of their own: connect it, then send Replo a prompt that uses it.
 * One list names each one's slug, the check that proves it connected and its reward, so the three cannot drift.
 * The workflows themselves are in `integrationWorkflows.ts`.
 */
export const INTEGRATION_WORKFLOW_KEYS = [
  "klaviyo",
  "meta-marketing-api",
  "google-ads",
  "google-analytics",
  "google-search-console",
  "figma",
  "notion",
  "linear",
  "posthog",
  "yotpo",
  "recharge",
  "triple-whale",
] as const satisfies readonly KnownIntegrationKey[];

export type IntegrationWorkflowKey = (typeof INTEGRATION_WORKFLOW_KEYS)[number];

export const INTEGRATION_WORKFLOW_SLUGS = INTEGRATION_WORKFLOW_KEYS.map(
  (key) => `use-${key}` as const,
);

export const INTEGRATION_CONNECTED_CHECK_NAMES = INTEGRATION_WORKFLOW_KEYS.map(
  (key) => `connected-${key}` as const,
);

export const INTEGRATION_REWARD_KEYS = INTEGRATION_WORKFLOW_KEYS.map(
  (key) => `home-use-${key}` as const,
);

/** Each integration as its own product names it. */
export const INTEGRATION_WORKFLOW_NAMES: Record<
  IntegrationWorkflowKey,
  string
> = {
  klaviyo: "Klaviyo",
  "meta-marketing-api": "Meta Ads",
  "google-ads": "Google Ads",
  "google-analytics": "Google Analytics",
  "google-search-console": "Google Search Console",
  figma: "Figma",
  notion: "Notion",
  linear: "Linear",
  posthog: "PostHog",
  yotpo: "Yotpo",
  recharge: "Recharge",
  "triple-whale": "Triple Whale",
};

/**
 * One entry per integration under `<prefix><key>`, for the records that must hold every slug, check or reward.
 * Parsed against the keys so the record's type is proven rather than asserted.
 */
export function byIntegrationWorkflow<Prefix extends string, Value>({
  prefix,
  value,
}: {
  prefix: Prefix;
  value: (key: IntegrationWorkflowKey) => Value;
}) {
  const keys = INTEGRATION_WORKFLOW_KEYS.map(
    (key) => `${prefix}${key}` as const,
  );
  return z
    .record(z.enum(keys), z.custom<Value>())
    .parse(
      Object.fromEntries(
        INTEGRATION_WORKFLOW_KEYS.map((key) => [`${prefix}${key}`, value(key)]),
      ),
    );
}
